'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import merge from 'hs-lodash/merge';
import styled, { ThemeProvider } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import usageTracker from '../../lib/usageTracker';
import ChecklistPage from './ChecklistPage';
import LoadingPage from './LoadingPage';
import MobilePage from './MobilePage';
import OverallPage from './OverallPage';
import PerformancePage from './PerformancePage';
import SecurityPage from './SecurityPage';
import SeoPage from './SeoPage';
import OverallGrade from '../sidepanel/OverallGrade';
import GraderActionCreator from '../../actions/GraderActionCreator';
import PreviewActionCreator from '../../actions/PreviewActionCreator';
import Logo from '../Logo';
import { getWebsiteGrade } from '../../selectors/Grader';
import TwoColumnLayout from '../layouts/TwoColumnLayout';
import { defaultTheme } from '../../theming/themes/default';
import { colors } from '../../theming/themes/colors';
import ctaBlob from 'bender-url!../../../img/cta-blobs.png';
import useWindowPostMessage from '../../hooks/useWindowPostMessage';
var WidgetTrialCTAPageWrapper = styled.div.withConfig({
  displayName: "WidgetResultsPage__WidgetTrialCTAPageWrapper",
  componentId: "mby8oi-0"
})(["background:url(", ") right bottom no-repeat;background-color:", ";color:", ";text-align:left;font-size:16px;"], ctaBlob, colors.PICKLEDBLUEWOODALT, colors.PRIMARYWHITE);
var HeaderToolbarWrapper = styled.div.withConfig({
  displayName: "WidgetResultsPage__HeaderToolbarWrapper",
  componentId: "mby8oi-1"
})(["position:absolute;top:0;width:100%;margin-top:40px;& > div{display:flex;justify-content:center;flex-direction:row;align-content:stretch;flex-wrap:nowrap;align-items:center;}& a{margin:0 1em;padding:0;color:", ";}"], colors.PRIMARYWHITE);
var FooterToolbarWrapper = styled.div.withConfig({
  displayName: "WidgetResultsPage__FooterToolbarWrapper",
  componentId: "mby8oi-2"
})(["height:200px;display:flex;flex-direction:row;align-items:baseline;justify-content:center;margin:auto;padding:50px 0 50px 0;"]);

var derivedTheme = function derivedTheme(config) {
  if (!config) return defaultTheme;
  var theme = {};
  var resultsPage = {};

  if (config.logo) {
    theme.logo = {
      url: config.logo
    };
  }

  if (config.title) {
    theme.title = config.title;
  }

  if (config.resultLinearGradient && config.resultLinearGradient.angle && config.resultLinearGradient.colorStop1 && config.resultLinearGradient.colorStop2) {
    var _config$resultLinearG = config.resultLinearGradient,
        angle = _config$resultLinearG.angle,
        colorStop1 = _config$resultLinearG.colorStop1,
        colorStop2 = _config$resultLinearG.colorStop2;
    resultsPage.overallResults = {
      background: "linear-gradient(" + angle + "deg, " + colorStop1 + " 0%, " + colorStop2 + " 80%)"
    };
  }

  if (config.footerLinks) {
    theme.footerLinks = config.footerLinks;
  }

  if (config.headerLinks) {
    theme.headerLinks = config.headerLinks;
  }

  return merge({}, defaultTheme, theme, {
    resultsPage: resultsPage
  });
};

var HeaderLinks = function HeaderLinks(_ref) {
  var _ref$config = _ref.config,
      config = _ref$config === void 0 ? [] : _ref$config;
  return /*#__PURE__*/_jsx(HeaderToolbarWrapper, {
    children: /*#__PURE__*/_jsx("div", {
      children: config.map(function (item, i) {
        return /*#__PURE__*/_jsx("a", {
          href: item.url,
          target: "_blank",
          rel: "noopener noreferrer",
          children: item.name
        }, i);
      })
    })
  });
};

var FooterLinks = function FooterLinks(_ref2) {
  var _ref2$config = _ref2.config,
      config = _ref2$config === void 0 ? [] : _ref2$config;
  return /*#__PURE__*/_jsx(FooterToolbarWrapper, {
    children: config.map(function (item, i) {
      return /*#__PURE__*/_jsx("a", {
        className: "button white",
        href: item.url,
        target: "_blank",
        rel: "noopener noreferrer",
        children: item.name
      }, i);
    })
  });
};

var usePostMessageHandling = function usePostMessageHandling(_ref3) {
  var websiteGrade = _ref3.websiteGrade;
  var gradesRef = useRef(websiteGrade);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      config = _useState2[0],
      setConfig = _useState2[1];

  var _useWindowPostMessage = useWindowPostMessage(function (_ref4) {
    var data = _ref4.data;

    switch (data.type) {
      case 'GRADER_STYLE_UPDATE':
        {
          setConfig(data.payload);
          postMessage({
            type: 'GRADER_STYLE_UPDATE_APPLIED'
          });
          return;
        }

      case 'GRADER_REQUEST_RESULTS':
        postMessage({
          type: 'GRADER_RESULTS',
          payload: gradesRef.current
        });
        return;

      default:
        return;
    }
  }),
      _useWindowPostMessage2 = _slicedToArray(_useWindowPostMessage, 1),
      postMessage = _useWindowPostMessage2[0];

  useEffect(function () {
    gradesRef.current = websiteGrade;
  }, [websiteGrade]);
  useEffect(function () {
    postMessage({
      type: 'GRADER_LOADED'
    });
  }, []);
  return {
    config: config
  };
};

var WidgetHeaderToolbar = function WidgetHeaderToolbar(_ref5) {
  var toolbar = _ref5.toolbar;
  return /*#__PURE__*/_jsxs("div", {
    className: "main-toolbar",
    children: [/*#__PURE__*/_jsx("div", {
      className: "logo-left",
      children: /*#__PURE__*/_jsx(Logo, {})
    }), toolbar]
  });
};

var Results = function Results(_ref6) {
  var websiteGrade = _ref6.websiteGrade,
      config = _ref6.config;
  return /*#__PURE__*/_jsx(TwoColumnLayout, {
    "data-test-id": "results-page",
    leftContent: /*#__PURE__*/_jsx(OverallGrade, {
      websiteGrade: websiteGrade,
      hideCTAs: true
    }),
    rightContent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(WidgetHeaderToolbar, {
        toolbar: /*#__PURE__*/_jsx(HeaderLinks, {
          config: config && config.headerLinks
        })
      }), /*#__PURE__*/_jsx(OverallPage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(PerformancePage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(SeoPage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(MobilePage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(SecurityPage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(ChecklistPage, {
        websiteGrade: websiteGrade,
        hideCTAs: true
      }), /*#__PURE__*/_jsx(WidgetTrialCTAPageWrapper, {
        children: /*#__PURE__*/_jsx(FooterLinks, {
          config: config && config.footerLinks
        })
      })]
    })
  });
};

var WidgetResultsPage = function WidgetResultsPage() {
  var dispatch = useDispatch();
  var websiteGrade = useSelector(getWebsiteGrade);

  var _useParams = useParams(),
      websiteUrl = _useParams.websiteUrl;

  var loadWebsiteGrade = function loadWebsiteGrade() {
    dispatch(GraderActionCreator.getGrade(websiteUrl));
    dispatch(PreviewActionCreator.previewWebsite(websiteUrl));
  };

  var _usePostMessageHandli = usePostMessageHandling({
    websiteGrade: websiteGrade
  }),
      config = _usePostMessageHandli.config;

  var theme = derivedTheme(config);
  useEffect(function () {
    usageTracker.logView('Results', {
      theme: theme.name
    });
    loadWebsiteGrade();
    return function () {
      return dispatch(GraderActionCreator.resultsPageUnmounted());
    };
  }, []);
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: theme,
    children: websiteGrade.loading ? /*#__PURE__*/_jsx(LoadingPage, {
      websiteUrl: websiteUrl
    }) : /*#__PURE__*/_jsx(Results, {
      websiteGrade: websiteGrade,
      config: config
    })
  });
};

export default WidgetResultsPage;